import { useState, useCallback, useRef } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { useClaimPublicContextSessionByPhoneMutation, PublicContextSessionStatus } from 'types/graphql';
import { MeConsumer } from 'shared/graphql/user/queries/me-consumer.gql';
import useStores from 'shared/hooks/use-stores';

type PhoneLoginState = {
  status: 'error' | 'idle' | 'loading' | 'success';
  errorMessage: string | null;
};

type UsePhoneNumberLoginReturnType = {
  loginState: PhoneLoginState;
  handlePhoneLogin: (phoneNumber: string) => Promise<void>;
  resetLoginState: () => void;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  userFirstName: string;
};

/**
 * Hook to handle phone number login for kiosk.
 * Claims a public context session by phone number and logs in the user.
 */
export function usePhoneNumberLogin(sessionId: string): UsePhoneNumberLoginReturnType {
  const { User } = useStores();
  const apolloClient = useApolloClient();
  const isLoggingInRef = useRef(false);

  const [loginState, setLoginState] = useState<PhoneLoginState>({
    status: 'idle',
    errorMessage: null,
  });

  const [claimSessionByPhone] = useClaimPublicContextSessionByPhoneMutation();

  const resetLoginState = useCallback((): void => {
    setLoginState({ status: 'idle', errorMessage: null });
  }, []);

  const handlePhoneLogin = useCallback(
    async (phoneNumber: string): Promise<void> => {
      if (isLoggingInRef.current || !sessionId) {
        return;
      }

      try {
        isLoggingInRef.current = true;
        setLoginState({ status: 'loading', errorMessage: null });

        // Claim the session by phone number
        const result = await claimSessionByPhone({
          variables: {
            id: sessionId,
            phone: phoneNumber,
          },
        });

        const session = result.data?.claimPublicContextSessionByPhone;

        if (!session) {
          setLoginState({
            status: 'error',
            errorMessage: 'Unable to find account with this phone number. Please try again or create an account.',
          });
          return;
        }

        if (session.status === PublicContextSessionStatus.expired) {
          setLoginState({
            status: 'error',
            errorMessage: 'Session has expired. Please start over.',
          });
          return;
        }

        if (!session.accessToken) {
          setLoginState({
            status: 'error',
            errorMessage: 'Unable to authenticate. Please try again.',
          });
          return;
        }

        // Get user data using the access token
        const { data: userData } = await apolloClient.query({
          query: MeConsumer,
          fetchPolicy: 'no-cache',
          context: {
            headers: {
              authorization: session.accessToken,
            },
          },
        });

        // Login the user
        await User.login({
          accessToken: session.accessToken,
          user: userData.meConsumer,
        });

        setLoginState({ status: 'success', errorMessage: null });
      } catch (error) {
        console.error('Error logging in with phone number:', error);
        setLoginState({ status: 'error', errorMessage: 'Oops! Something went wrong looking up that number' });
      } finally {
        isLoggingInRef.current = false;
      }
    },
    [sessionId, claimSessionByPhone, apolloClient, User]
  );

  return {
    loginState,
    handlePhoneLogin,
    resetLoginState,
    isLoading: loginState.status === 'loading',
    isSuccess: loginState.status === 'success',
    isError: loginState.status === 'error',
    userFirstName: User?.firstName ?? '',
  };
}
